import React, { useEffect, useState } from 'react'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { Typography, notification } from 'antd'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

import {
  forgotEmailSchema,
  checkOtpSchema,
  resetPasswordWithTokenSchema,
  authApi,
} from '@/api/auth.api'
import { isInitializedAtom, isAuthenticatedAtom } from '@/store/auth.atom'
import { AuthLayout } from './AuthLayout'
import { LoginFormView } from './LoginFormView'
import { OtpInput } from './OtpInput'
import { PasswordField } from './PasswordField'

const { Text } = Typography

type AuthView =
  | 'login'
  | 'forgot-email'
  | 'forgot-otp'
  | 'forgot-new-password'
  | 'success'

export function LoginPage() {
  const { redirect } = useSearch({ from: '/(public)/login' })
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isInitialized = useAtomValue(isInitializedAtom)

  const [view, setView] = useState<AuthView>('login')

  // Forgot Password States
  const [forgotEmail, setForgotEmail] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [resetToken, setResetToken] = useState('')

  const [isRequestingOTP, setIsRequestingOTP] = useState(false)
  const [isCheckingOTP, setIsCheckingOTP] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      navigate({ to: redirect || '/dashboards', replace: true })
    }
  }, [isInitialized, isAuthenticated, navigate, redirect])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000)
    return () => clearInterval(timer)
  }, [countdown])

  const validatePasswordRealtime = (
    field: 'password' | 'confirmPassword',
    val: string,
  ) => {
    const formData = {
      email: forgotEmail || 'dummy@example.com',
      password: field === 'password' ? val : newPassword,
      confirmPassword: field === 'confirmPassword' ? val : confirmPassword,
      token: resetToken || 'dummy-token',
    }

    const result = resetPasswordWithTokenSchema.safeParse(formData)

    if (!result.success) {
      const fieldIssue = result.error.issues.find((i) => i.path[0] === field)
      const confirmIssue = result.error.issues.find(
        (i) => i.path[0] === 'confirmPassword',
      )

      setErrors((prev) => ({
        ...prev,
        [field]: fieldIssue?.message,
        ...(field === 'password' && formData.confirmPassword
          ? { confirmPassword: confirmIssue?.message }
          : {}),
      }))
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
        ...(field === 'password' ? { confirmPassword: undefined } : {}),
      }))
    }
  }

  // Handlers
  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setErrors({})

    if (!forgotEmail.trim()) {
      setErrors({ email: 'Vui lòng nhập email' })
      return
    }

    if (countdown > 0) {
      setView('forgot-otp')
      return
    }

    const validationResult = forgotEmailSchema.safeParse({ email: forgotEmail })
    if (!validationResult.success) {
      const fieldErrors: Partial<Record<string, string>> = {}
      for (const issue of validationResult.error.issues) {
        const field = String(issue.path[0])
        if (!fieldErrors[field]) fieldErrors[field] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setIsRequestingOTP(true)
    try {
      const res: any = await authApi.requestPasswordReset(validationResult.data)
      setView('forgot-otp')
      setCountdown(60)
      setOtp(['', '', '', '', '', ''])
      notification.success({
        title: res?.responseMessage || 'Đã gửi mã OTP đến email của bạn.',
      })
    } catch (error: any) {
      notification.error({
        title: error?.message || 'Đã xảy ra lỗi khi yêu cầu gửi OTP.',
      })
    } finally {
      setIsRequestingOTP(false)
    }
  }

  const handleCheckOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const validationResult = checkOtpSchema.safeParse({
      email: forgotEmail,
      otp: otp.join(''),
    })
    if (!validationResult.success) {
      const fieldErrors: Partial<Record<string, string>> = {}
      for (const issue of validationResult.error.issues) {
        const field = String(issue.path[0])
        if (!fieldErrors[field]) fieldErrors[field] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setIsCheckingOTP(true)
    try {
      const res: any = await authApi.checkOtp(validationResult.data)
      const token =
        typeof res === 'string'
          ? res
          : res?.token || res?.data || res?.responseData || res?.result || ''
      if (!token) throw new Error('Không nhận được token xác thực từ hệ thống.')

      setResetToken(token)
      setView('forgot-new-password')
    } catch (error: any) {
      notification.error({
        title: error?.message || 'Đã xảy ra lỗi khi nhập OTP.',
      })
    } finally {
      setIsCheckingOTP(false)
    }
  }

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const validationResult = resetPasswordWithTokenSchema.safeParse({
      email: forgotEmail,
      password: newPassword,
      confirmPassword,
      token: resetToken,
    })

    if (!validationResult.success) {
      const fieldErrors: Partial<Record<string, string>> = {}
      for (const issue of validationResult.error.issues) {
        const field = String(issue.path[0])
        if (!fieldErrors[field]) fieldErrors[field] = issue.message
      }
      setErrors(fieldErrors)
      return
    }

    setIsResettingPassword(true)
    try {
      const res: any = await authApi.resetPasswordWithToken(
        validationResult.data,
      )
      setView('success')
      notification.success({
        title: res?.responseMessage || 'Cập nhật mật khẩu thành công.',
      })
    } catch (error: any) {
      notification.error({
        title: error?.message || 'Đã xảy ra lỗi khi cập nhật mật khẩu.',
      })
    } finally {
      setIsResettingPassword(false)
    }
  }

  // Layout metadata config
  const viewConfig: Record<
    AuthView,
    { title: string; subtitle?: React.ReactNode }
  > = {
    login: { title: 'Đăng nhập' },
    'forgot-email': {
      title: 'Quên mật khẩu',
      subtitle: (
        <Text className="mt-1 block text-xs text-gray-400">
          Nhập email đã đăng ký để tạo lại mật khẩu.
        </Text>
      ),
    },
    'forgot-otp': { title: 'Xác nhận OTP' },
    'forgot-new-password': { title: 'Tạo mật khẩu' },
    success: {
      title: 'Thành công',
      subtitle: (
        <Text className="mt-1 block text-xs sm:text-sm">
          Cập nhật mật khẩu thành công.
          <br />
          Hãy đăng nhập lại với mật khẩu mới.
        </Text>
      ),
    },
  }

  return (
    <AuthLayout
      title={viewConfig[view].title}
      subtitle={viewConfig[view].subtitle}
    >
      {view === 'login' && (
        <LoginFormView
          redirectTo={redirect || '/dashboards'}
          onForgotPassword={() => {
            setView('forgot-email')
            setErrors({})
          }}
        />
      )}

      {view === 'forgot-email' && (
        <form onSubmit={handleRequestOtp} noValidate>
          <div className="flex flex-col gap-3.5 sm:gap-4">
            <div>
              <label
                htmlFor="forgot-email-input"
                className="mb-1.5 block text-sm sm:text-base font-medium"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                id="forgot-email-input"
                type="email"
                size="large"
                value={forgotEmail}
                onChange={(e: any) => {
                  setForgotEmail(e.target.value)
                  if (errors.email)
                    setErrors((prev) => ({ ...prev, email: undefined }))
                }}
                placeholder="Tên email"
                autoComplete="off"
              />
              {errors.email && (
                <Text type="danger" className="mt-1 block text-xs sm:text-sm">
                  {errors.email}
                </Text>
              )}
            </div>
            <div className="flex gap-2.5 sm:gap-3 mt-1">
              <Button
                variant="outline"
                size="lg"
                className="flex-1 text-[14px]"
                onClick={() => {
                  setView('login')
                  setErrors({})
                }}
                disabled={isRequestingOTP}
              >
                Quay lại
              </Button>
              <Button
                htmlType="submit"
                size="lg"
                variant="primary"
                className="flex-1 text-[14px]"
                loading={isRequestingOTP}
              >
                Xác nhận
              </Button>
            </div>
          </div>
        </form>
      )}

      {view === 'forgot-otp' && (
        <form onSubmit={handleCheckOtpSubmit}>
          <div className="flex flex-col gap-3.5 sm:gap-4">
            <OtpInput
              value={otp}
              onChange={setOtp}
              onClearError={() =>
                errors.otp && setErrors((prev) => ({ ...prev, otp: undefined }))
              }
            />
            {errors.otp && (
              <Text
                type="danger"
                className="-mt-1 block text-center text-xs sm:text-sm"
              >
                {errors.otp}
              </Text>
            )}
            <div className="text-right">
              <button
                type="button"
                onClick={() => handleRequestOtp()}
                disabled={isRequestingOTP || isCheckingOTP || countdown > 0}
                className="cursor-pointer border-none bg-transparent text-[#0088FF] hover:underline disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline"
              >
                {countdown > 0 ? `Gửi lại (${countdown}s)` : 'Gửi lại'}
              </button>
            </div>
            <div className="flex gap-2.5 sm:gap-3 mt-1">
              <Button
                variant="outline"
                size="lg"
                className="flex-1 text-[14px]"
                onClick={() => {
                  setView('forgot-email')
                  setErrors({})
                  setOtp(['', '', '', '', '', ''])
                }}
                disabled={isCheckingOTP}
              >
                Quay lại
              </Button>
              <Button
                htmlType="submit"
                size="lg"
                variant="primary"
                className="flex-1 text-[14px]"
                loading={isCheckingOTP}
                disabled={otp.join('').length < 6 || isCheckingOTP}
              >
                Tiếp tục
              </Button>
            </div>
          </div>
        </form>
      )}

      {view === 'forgot-new-password' && (
        <form onSubmit={handleResetPasswordSubmit}>
          <div className="flex flex-col gap-3.5 sm:gap-4">
            <PasswordField
              label="Mật khẩu mới"
              required
              value={newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value
                setNewPassword(val)
                validatePasswordRealtime('password', val)
              }}
              placeholder="••••••••"
              autoComplete="new-password"
              error={errors.password}
            />

            <PasswordField
              label="Xác nhận lại mật khẩu"
              required
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value
                setConfirmPassword(val)
                validatePasswordRealtime('confirmPassword', val)
              }}
              placeholder="••••••••"
              autoComplete="new-password"
              error={errors.confirmPassword}
            />

            <div className="flex gap-2.5 sm:gap-3 mt-1">
              <Button
                variant="outline"
                size="lg"
                className="flex-1 text-[14px]"
                onClick={() => {
                  setView('forgot-otp')
                  setErrors({})
                }}
                disabled={isResettingPassword}
              >
                Quay lại
              </Button>
              <Button
                htmlType="submit"
                variant="primary"
                size="lg"
                className="flex-1 text-[14px]"
                loading={isResettingPassword}
              >
                Xác nhận
              </Button>
            </div>
          </div>
        </form>
      )}

      {view === 'success' && (
        <div className="mt-4 sm:mt-6 flex flex-col gap-4">
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              setView('login')
              setForgotEmail('')
              setOtp(['', '', '', '', '', ''])
              setNewPassword('')
              setConfirmPassword('')
              setResetToken('')
              setCountdown(0)
              setErrors({})
            }}
          >
            Đăng nhập
          </Button>
        </div>
      )}
    </AuthLayout>
  )
}
